import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Field, Notice } from '@ailab/ui';
import {
  cursorPageSchema,
  invitationRedeemResponseSchema,
  sessionSchema,
  totpSetupResponseSchema,
  userSchema,
  type User,
} from '@ailab/contracts';
import { ME_QUERY_KEY, useMe, useRefreshMe } from '../../lib/account';
import { ApiRequestError, apiRequest, errorMessage } from '../../lib/http';
import { BalanceSection } from './BalanceSection';
import styles from './account.module.css';

const sessionsPage = cursorPageSchema(sessionSchema);

function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  async function run(action: () => Promise<string | undefined>) {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      const message = await action();
      if (message) setDone(message);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, done, run };
}

function ProfileSection({ user }: { user: User }) {
  const refreshMe = useRefreshMe();
  const [form, setForm] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    skoolName: user.skoolName,
  });
  const action = useAction();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void action.run(async () => {
      await apiRequest('/me/profile', userSchema, { method: 'PATCH', body: form });
      await refreshMe();
      return 'Perfil guardado.';
    });
  };
  return (
    <Card>
      <h2 className={styles.sectionTitle}>Perfil</h2>
      <form className={styles.form} onSubmit={submit} noValidate>
        <div className={styles.grid}>
          <Field
            label="Nombre"
            autoComplete="given-name"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <Field
            label="Apellidos"
            autoComplete="family-name"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </div>
        <Field
          label="Nombre en Skool"
          value={form.skoolName}
          onChange={(e) => setForm({ ...form, skoolName: e.target.value })}
        />
        <Field
          label="Email"
          value={user.email}
          readOnly
          hint="El cambio de email llegará más adelante."
        />
        {action.error ? <Notice tone="error">{action.error}</Notice> : null}
        {action.done ? <Notice tone="success">{action.done}</Notice> : null}
        <div>
          <Button type="submit" busy={action.busy}>
            Guardar perfil
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordSection() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const action = useAction();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void action.run(async () => {
      if (form.newPassword !== form.confirm) {
        throw new ApiRequestError(400, 'validation_error', 'Las dos contraseñas no coinciden.');
      }
      await apiRequest('/me/password', null, {
        method: 'POST',
        body: { currentPassword: form.currentPassword, newPassword: form.newPassword },
      });
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
      return 'Contraseña actualizada. Se han cerrado tus otras sesiones.';
    });
  };
  const mismatch = form.confirm !== '' && form.confirm !== form.newPassword;
  return (
    <Card>
      <h2 className={styles.sectionTitle}>Contraseña</h2>
      <form className={styles.form} onSubmit={submit} noValidate>
        <Field
          label="Contraseña actual"
          type="password"
          autoComplete="current-password"
          value={form.currentPassword}
          onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
        />
        <Field
          label="Nueva contraseña"
          type="password"
          autoComplete="new-password"
          hint="Al menos 10 caracteres."
          value={form.newPassword}
          onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
        />
        <Field
          label="Repite la nueva contraseña"
          type="password"
          autoComplete="new-password"
          value={form.confirm}
          error={mismatch ? 'Las dos contraseñas no coinciden.' : undefined}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })}
        />
        {action.error ? <Notice tone="error">{action.error}</Notice> : null}
        {action.done ? <Notice tone="success">{action.done}</Notice> : null}
        <div>
          <Button type="submit" busy={action.busy} disabled={mismatch}>
            Cambiar contraseña
          </Button>
        </div>
      </form>
    </Card>
  );
}

function MfaSection({ enabled }: { enabled: boolean }) {
  const refreshMe = useRefreshMe();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string }>();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const action = useAction();
  return (
    <Card>
      <h2 className={styles.sectionTitle}>Verificación en dos pasos</h2>
      {enabled ? (
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void action.run(async () => {
              await apiRequest('/me/mfa/totp/disable', null, {
                method: 'POST',
                body: { password, code },
              });
              setCode('');
              setPassword('');
              await refreshMe();
              return 'Verificación en dos pasos desactivada.';
            });
          }}
        >
          <Notice tone="success">
            Activada. Al entrar te pediremos un código de tu aplicación.
          </Notice>
          <Field
            label="Contraseña"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Field
            label="Código actual"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {action.error ? <Notice tone="error">{action.error}</Notice> : null}
          <div>
            <Button type="submit" variant="secondary" busy={action.busy}>
              Desactivar
            </Button>
          </div>
        </form>
      ) : setup ? (
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void action.run(async () => {
              await apiRequest('/me/mfa/totp/enable', null, { method: 'POST', body: { code } });
              setSetup(undefined);
              setCode('');
              await refreshMe();
              return 'Verificación en dos pasos activada.';
            });
          }}
        >
          <p>
            Añade esta clave en tu aplicación de autenticación (Google Authenticator, 1Password,
            etc.) o{' '}
            <a className={styles.inlineLink} href={setup.otpauthUrl}>
              ábrela en la aplicación
            </a>
            .
          </p>
          <p className={styles.secret} aria-label="Clave para la aplicación">
            {setup.secret}
          </p>
          <Field
            label="Código de la aplicación"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {action.error ? <Notice tone="error">{action.error}</Notice> : null}
          <div>
            <Button type="submit" busy={action.busy}>
              Activar
            </Button>
          </div>
        </form>
      ) : (
        <div className={styles.form}>
          <p>
            Protege tu cuenta con un código temporal además de la contraseña. Es obligatorio para
            administración.
          </p>
          {action.error ? <Notice tone="error">{action.error}</Notice> : null}
          {action.done ? <Notice tone="success">{action.done}</Notice> : null}
          <div>
            <Button
              type="button"
              variant="secondary"
              busy={action.busy}
              onClick={() =>
                void action.run(async () => {
                  setSetup(
                    await apiRequest('/me/mfa/totp/setup', totpSetupResponseSchema, {
                      method: 'POST',
                    }),
                  );
                })
              }
            >
              Configurar
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function InvitationSection() {
  const refreshMe = useRefreshMe();
  const [code, setCode] = useState('');
  const action = useAction();
  return (
    <Card>
      <h2 className={styles.sectionTitle}>Activa tu acceso a AILAB</h2>
      <form
        className={styles.form}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void action.run(async () => {
            await apiRequest('/me/invitation', invitationRedeemResponseSchema, {
              method: 'POST',
              body: { code },
            });
            await refreshMe();
            return 'Acceso activado.';
          });
        }}
      >
        <p>Tu cuenta todavía no tiene acceso a AILAB. Introduce tu código de invitación.</p>
        <Field
          label="Código de invitación"
          placeholder="AILAB-XXXX-XXXX-XXXX"
          autoComplete="off"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        {action.error ? <Notice tone="error">{action.error}</Notice> : null}
        {action.done ? <Notice tone="success">{action.done}</Notice> : null}
        <div>
          <Button type="submit" busy={action.busy}>
            Activar acceso
          </Button>
        </div>
      </form>
    </Card>
  );
}

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

function SessionsSection() {
  const sessions = useInfiniteQuery({
    queryKey: ['me', 'sessions'],
    queryFn: ({ pageParam }) =>
      apiRequest(`/me/sessions?limit=20${pageParam ? `&cursor=${pageParam}` : ''}`, sessionsPage),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const action = useAction();
  const items = sessions.data?.pages.flatMap((page) => page.items) ?? [];
  const reload = () => sessions.refetch();
  return (
    <Card>
      <h2 className={styles.sectionTitle}>Sesiones activas</h2>
      {sessions.isPending ? <p role="status">Cargando sesiones…</p> : null}
      {sessions.isError ? <Notice tone="error">{errorMessage(sessions.error)}</Notice> : null}
      <ul className={styles.sessionList}>
        {items.map((session) => (
          <li key={session.id} className={styles.session}>
            <div>
              <strong>{session.device}</strong>
              {session.current ? <span className={styles.badge}>Esta sesión</span> : null}
              <div className={styles.sessionMeta}>
                Último uso: {dateFormat.format(new Date(session.lastSeenAt))} · Inicio:{' '}
                {dateFormat.format(new Date(session.createdAt))}
              </div>
            </div>
            {session.current ? null : (
              <Button
                type="button"
                variant="secondary"
                disabled={action.busy}
                aria-label={`Cerrar la sesión de ${session.device}`}
                onClick={() =>
                  void action.run(async () => {
                    await apiRequest(`/me/sessions/${session.id}`, null, { method: 'DELETE' });
                    await reload();
                  })
                }
              >
                Cerrar
              </Button>
            )}
          </li>
        ))}
      </ul>
      {action.error ? <Notice tone="error">{action.error}</Notice> : null}
      <div className={styles.row}>
        {sessions.hasNextPage ? (
          <Button type="button" variant="ghost" onClick={() => void sessions.fetchNextPage()}>
            Ver más
          </Button>
        ) : null}
        {items.length > 1 ? (
          <Button
            type="button"
            variant="secondary"
            disabled={action.busy}
            onClick={() =>
              void action.run(async () => {
                await apiRequest('/me/sessions', null, { method: 'DELETE' });
                await reload();
              })
            }
          >
            Cerrar las demás sesiones
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

export function AccountPage() {
  const me = useMe();
  const navigate = useNavigate();
  const client = useQueryClient();
  const logout = useAction();

  if (me.isPending) return <p role="status">Cargando tu cuenta…</p>;
  if (me.isError) return <Notice tone="error">{errorMessage(me.error)}</Notice>;
  if (!me.data) {
    return (
      <>
        <h1>Cuenta</h1>
        <Card className={styles.narrow}>
          <p>Entra para ver tu perfil, tus sesiones y la seguridad de tu cuenta.</p>
          <div className={styles.row}>
            <Link to="/entrar">Entrar</Link>
            <Link to="/registro">Crear cuenta</Link>
          </div>
        </Card>
      </>
    );
  }

  const { user, mfa, access } = me.data;
  return (
    <>
      <h1>Cuenta</h1>
      <p className={styles.lead}>
        {user.firstName} {user.lastName} · {user.email}
      </p>
      <div className={styles.sections}>
        {access.blocked ? (
          <Notice tone="error">
            Tu acceso a AILAB está pendiente de revisión por administración. Contacta desde la
            comunidad para verificar tu membresía.
          </Notice>
        ) : access.platform ? null : (
          <InvitationSection />
        )}
        {access.requiresMfa.length > 0 ? (
          <Notice tone="info">
            Tu cuenta tiene permisos de administración. Activa la verificación en dos pasos y entra
            con ella para usarlos.
          </Notice>
        ) : null}
        <BalanceSection />
        <ProfileSection user={user} />
        <PasswordSection />
        <MfaSection enabled={mfa.enabled} />
        <SessionsSection />
        <div>
          <Button
            type="button"
            variant="secondary"
            busy={logout.busy}
            onClick={() =>
              void logout.run(async () => {
                await apiRequest('/auth/logout', null, { method: 'POST' });
                client.setQueryData(ME_QUERY_KEY, null);
                await navigate({ to: '/entrar' });
              })
            }
          >
            Salir
          </Button>
        </div>
      </div>
    </>
  );
}
